import { ReactNode, useEffect, useMemo, useCallback, useState } from 'react';
import {
  Alignment,
  Fit,
  Layout,
  useRive,
  Rive,
  decodeFont,
  FontAsset,
  useViewModelInstanceTrigger,
  ViewModelInstance,
} from '@rive-app/react-canvas';
import sortBy from 'lodash/sortBy';

import { TextRun } from './rive.constants';
import * as Styled from './rive-animation.style';
import {
  ActivityPosition,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import { FONTS_BY_LANGUAGE } from 'approot/shared/styles/fonts/remote-fonts';
import { logException } from 'approot/shared/debug';
import { isInOldRiveFormat, useStateMachineInputs } from './rive.utils';

type StateMachineInputs = ReturnType<typeof useStateMachineInputs>;

type Props = {
  link: string;
  width: number;
  height: number;
  altText?: string | null;
  animationAutoPlay?: boolean;
  textRuns?: TextRun[];
  buttons?: TextRun[];
  buttonsPosition?: ActivityPosition | null;
  language?: LanguageCodes;
  isCovered?: boolean;
  controls?: (
    rive: Rive | null,
    viewModelInstance: ViewModelInstance | null,
    stateMachineInputs: StateMachineInputs
  ) => ReactNode;
};

const RiveAnimation = ({
  link,
  width,
  height,
  altText,
  animationAutoPlay,
  textRuns,
  buttons,
  buttonsPosition,
  language = LANGUAGE_CODE_EN_AU,
  isCovered = false,
  controls,
}: Props) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const loadFont = useCallback(async (asset: FontAsset, fontFamily: string) => {
    fetch(fontFamily)
      .then(async res => {
        const font = await decodeFont(new Uint8Array(await res.arrayBuffer()));
        asset.setFont(font);
        font.unref();
      })
      .catch(e => {
        logException({
          sourceFile: 'rive-animation.tsx',
          message: 'Error loading font for Rive',
          e,
        });
      });
  }, []);

  const { rive, RiveComponent } = useRive(
    {
      src: link,
      autoplay: animationAutoPlay,
      layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
      // This name will be attached to the main artboard in the Rive file
      stateMachines: 'State Machine 1',
      assetLoader: asset => {
        if (asset.isFont) {
          // if we are looking at a language which matches an item in RIVE_FONTS_BY_LANGUAGE, load the font
          // otherwise do nothing as the embedded calibre font will be used within the Rive file
          if (FONTS_BY_LANGUAGE[language]) {
            loadFont(asset as FontAsset, FONTS_BY_LANGUAGE[language]);
            return true;
          }
          return false;
        }
        return false;
      },
      autoBind: true,
      onLoad: () => setIsLoaded(true),
    },
    {
      shouldResizeCanvasToContainer: true,
    }
  );

  const stateMachineInputs = useStateMachineInputs(rive, 'State Machine 1');

  const stateMachineInput = useMemo(
    () => stateMachineInputs.find(input => input.name === 'Trigger 1') || null,
    [stateMachineInputs]
  );

  // get the view model instance and trigger
  // if data binding is being used for this file
  // isLoaded dependency ensures this re-computes when the Rive file finishes loading
  // (viewModelInstance is populated asynchronously after the file loads)
  const viewModelInstance = useMemo(() => {
    return isLoaded ? rive?.viewModelInstance || null : null;
  }, [rive, isLoaded]);

  const dataBindingTrigger = useViewModelInstanceTrigger(
    'Trigger 1',
    viewModelInstance
  );

  const sortedButtons = useMemo(() => sortBy(buttons, 'sort'), [buttons]);

  useEffect(() => {
    if (animationAutoPlay) {
      rive?.play();
    } else {
      rive?.pause();
    }
  }, [animationAutoPlay, rive]);

  useEffect(() => {
    if (rive && textRuns && textRuns.length > 0) {
      const sortedTextRuns = sortBy(textRuns, 'sort');
      const isOldRiveFormat = isInOldRiveFormat(rive);

      try {
        const viewModel = !isOldRiveFormat ? rive.viewModelInstance : undefined;
        const properties = viewModel?.properties.filter(
          p => p.type.toString() === 'string'
        );

        sortedTextRuns.forEach((textRun, index) => {
          if (isOldRiveFormat) {
            // fallback to the old method of setting text run values
            rive?.setTextRunValue(`run${index + 1}`, textRun.text);
          } else if (viewModel && properties && properties.length > 0) {
            const stringProperty = viewModel.string(properties[index].name);
            if (stringProperty) {
              stringProperty.value = textRun.text;
            }
          }
        });
      } catch {}
    }
  }, [textRuns, rive]);

  return (
    <>
      <RiveComponent
        key={`${textRuns
          ?.map(textRun => textRun.sort)
          .join('')}${sortedButtons?.map(button => button.sort).join('')}`}
        width={width}
        height={height}
        role="img"
        aria-label={altText || ''}
        aria-hidden={isCovered}
      />
      {(stateMachineInput || dataBindingTrigger) && (
        <Styled.RiveComponentTrigger
          data-testid="rive-component-trigger"
          type="button"
          tabIndex={isCovered ? -1 : 0}
          // tabIndex={-1} only leaves Tab order — without aria-hidden a screen
          // reader in browse mode can still reach and fire this trigger on the
          // animation the overlay is deliberately holding paused.
          aria-hidden={isCovered}
          aria-label={
            altText
              ? `Click to trigger animation for: ${altText}`
              : 'Click to trigger animation'
          }
          onClick={() =>
            stateMachineInput
              ? stateMachineInput.fire()
              : dataBindingTrigger
                ? dataBindingTrigger.trigger()
                : undefined
          }
        />
      )}
      {rive && sortedButtons.length > 0 && (
        <Styled.RiveButtons $position={buttonsPosition}>
          {sortedButtons.map((button, index) => (
            <RiveButton
              key={button.itemId}
              button={button}
              index={index}
              viewModelInstance={viewModelInstance}
              stateMachineInputs={stateMachineInputs}
              isCovered={isCovered}
            />
          ))}
        </Styled.RiveButtons>
      )}
      {controls && controls?.(rive, viewModelInstance, stateMachineInputs)}
    </>
  );
};

export default RiveAnimation;

const RiveButton = ({
  button,
  index,
  viewModelInstance,
  stateMachineInputs,
  isCovered,
}: {
  button: TextRun;
  index: number;
  viewModelInstance: ViewModelInstance | null;
  stateMachineInputs: StateMachineInputs;
  isCovered: boolean;
}) => {
  const stateMachineInput = useMemo(
    () =>
      stateMachineInputs.find(input => input.name === `btn${index + 1}`) ||
      null,
    [stateMachineInputs, index]
  );

  const trigger = useViewModelInstanceTrigger(
    `btn ${index + 1}`,
    viewModelInstance
  );

  return (
    <Styled.RiveButton
      data-selector="rive-btn-text"
      data-testid={`rive-button-${index}`}
      type="button"
      tabIndex={isCovered ? -1 : 0}
      aria-hidden={isCovered}
      onClick={() =>
        stateMachineInput
          ? stateMachineInput.fire()
          : trigger
            ? trigger.trigger()
            : undefined
      }
    >
      {button.text}
    </Styled.RiveButton>
  );
};
